import { createSelector } from '@reduxjs/toolkit';
import last from 'lodash/last';
import { NodeFlag } from '../../../general/model';
import { UserAccessRight } from '../../../general/model';
import { CHNode, CHNodeIsCreativeFolder } from '../../../general/node';
import { HasFlags } from '../../../general/model';
import { RootSelector, getStore } from '../../models/rootSelector';
import { BreadcrumbsShape, IBreadcrumb } from './model';
import { getShareID } from '../app/selectors';
import { routerFolder } from '../../../general/urls';
export const getBreadcrumbs: RootSelector<BreadcrumbsShape> = (store) => store.breadcrumbs;
export const getForceFullBreadcrumbs: RootSelector<boolean> = createSelector(
  getStore,
  (x) => x.env?.forceFullBreadcrumbs ?? false,
);

export const getFileManagerBreadcrumbs: RootSelector<IBreadcrumb[]> = createSelector(
  [getBreadcrumbs, getShareID],
  (breadcrumbs, shareId) => {
    return breadcrumbs.map((b) => ({
      id: b.id,
      link: routerFolder(b.id, shareId || ''),
      name: b.name,
      flags: b.flags,
    }));
  },
);

export const getCurrentNode: RootSelector<CHNode | null> = createSelector(
  [getBreadcrumbs],
  (breadcrumbs) => {
    if (breadcrumbs.length === 0) {
      return null;
    }
    return breadcrumbs[breadcrumbs.length - 1];
  },
);

// Текущая открытая папка имеет флаг «папка типа креатива». По нему решается
// видимость кнопок Convert и Pack Playable в тулбаре (требование 8 заказчика:
// «кнопки появляются только в папках с флагом Creative Type» — смотрим именно
// на текущий узел, не на путь целиком; MYUA-16744, редизайн, раздел 6.1).
//
// Источник — getCurrentNode: это единственное место стора, где открытая папка
// лежит целым узлом с flags (app.nodeId хранит только id, fileManager.nodes —
// детей папки, а не её саму). getRelativeLocation().onTypeLevel считает тот же
// факт, но собран из getFileManagerBreadcrumbs для нужд крошек и колонок —
// третью копию заводить не нужно, но и связывать доступность действий
// с производным селектором другого назначения тоже.
//
// Пока крошки новой папки не загрузились, селектор даёт false и кнопок нет.
// Это правильнее, чем показать их по устаревшим крошкам предыдущей папки.
export const getCurrentFolderIsCreativeType: RootSelector<boolean> = createSelector(
  getCurrentNode,
  (node) => node?.flags.includes(NodeFlag.folder_creative_type) ?? false,
);

export const getRelativeLocationForBreadcrumbs = (bc: HasFlags[]): RelativeLocationDescriptor => {
  const inCreative = bc.find((x) => x.flags.includes(NodeFlag.folder_creative)) !== undefined;
  const onTypeLevel = last(bc)?.flags.includes(NodeFlag.folder_creative_type) ?? false;
  const insideType =
    bc.find((x) => x.flags.includes(NodeFlag.folder_creative_type)) !== undefined && !onTypeLevel;
  return {
    insideType,
    inCreative,
    onTypeLevel,
  };
};

export type RelativeLocationDescriptor = {
  insideType: boolean; // внутри папок внутри типа
  inCreative: boolean; // в папке креатива
  onTypeLevel: boolean; // в папке типа
};

export const getRelativeLocation: RootSelector<RelativeLocationDescriptor> = createSelector(
  getFileManagerBreadcrumbs,
  getRelativeLocationForBreadcrumbs,
);
export const getCreativeFolder: RootSelector<CHNode | undefined> = createSelector(
  getBreadcrumbs,
  (breadcrumbs) => breadcrumbs.find(CHNodeIsCreativeFolder),
);
export const canUploadCreatives: RootSelector<boolean> = createSelector(
  getCreativeFolder,
  (folder) => (folder ? folder.perms.includes(UserAccessRight.write) : false),
);
